import React, { useEffect, useMemo, useRef } from 'react';
import { IconButton } from '@mui/material';
import { TbStatusChange } from 'react-icons/tb';
import { CornerBanner, Menu, MenuButton } from '@shared/components';
import { useConsumerType, useSanityLanguageCode } from '@retail/sanity/hooks';
import { useLatestChangelogEntriesIds } from '@retail/sanity/data-access';
import { useAlertedChangelogEntries } from './useAlertedChangelogEntries';
import { ChangelogEntriesMenuContent } from './ChangelogEntriesMenuContent';
import { useChangelogTFunction } from '@retail/changelog/i18n';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useChangelogDropdownStore } from './useChangelogDropdownStore';

export const ChangelogEntriesIconMenuDropdown: React.FC = () => {
  const t = useChangelogTFunction();
  const language = useSanityLanguageCode();
  const consumer = useConsumerType();
  const alertedChangelogEntries = useAlertedChangelogEntries();
  const { appTenant } = useSelectedOrgUnit();

  const iconRef = useRef<HTMLButtonElement>(null);
  const setOpen = useChangelogDropdownStore((state) => state.setOpen);

  useEffect(() => {
    setOpen(() => iconRef.current?.click());
    return () => setOpen(null);
  }, [setOpen]);

  const { data: changelogEntries = [] } = useLatestChangelogEntriesIds({
    maxAgeDays: 30,
    language,
    consumer,
    country: appTenant.countryCode,
  });

  const unseenEntriesCount = useMemo(
    () => changelogEntries.filter(({ id }) => !alertedChangelogEntries.hasValue(id)).length,
    [alertedChangelogEntries, changelogEntries]
  );

  return (
    <Menu slotProps={{ paper: { sx: { height: 800 } } }}>
      {({ isOpen }) => (
        <>
          <CornerBanner
            show={unseenEntriesCount > 0}
            text={unseenEntriesCount < 10 ? unseenEntriesCount : '9+'}
            ariaLabel={t('changelog.recentChanges.count')}
          >
            <MenuButton
              component={IconButton}
              ref={iconRef}
              sx={{ transform: 'rotate(90deg) scaleX(-1)' }}
            >
              <TbStatusChange />
            </MenuButton>
          </CornerBanner>
          <ChangelogEntriesMenuContent isOpen={isOpen} />
        </>
      )}
    </Menu>
  );
};
